import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-2">文章不存在</h2>
          <p className="text-foreground-500 mb-6">您访问的文章可能已被删除或链接无效</p>
          <Link
            to="/articles"
            className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回文章列表
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '文章资讯', path: '/articles' },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '抖阴pro',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-sm text-foreground-400">{article.date}</span>
                <span className="text-sm text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span className="text-sm font-medium text-foreground-700">{article.author}</span>
              </div>

              <div className="rounded-2xl overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-56 md:h-72 object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回文章列表
                </Link>
              </div>
            </div>
          </div>
        </article>
      </div>
    </>
  );
}